import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { supabase } from '@/integrations/supabase/client'
import { Users } from '@/integrations/supabase/types/tables'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'
import { Separator } from '@/components/ui/separator'
import { Code, Mail, Loader2 } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { AuthLegalNotice } from '@/components/legal/AuthLegalNotice'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const getURL = () => {
	// Always redirect to /auth/callback for proper token handling
	// The callback page will then redirect to the appropriate destination

	// For production domains
	if (
		window.location.hostname === 'www.feedbask.com' ||
		window.location.hostname === 'feedbask.com'
	) {
		console.log('[getURL] Matched production domain')
		return 'https://www.feedbask.com/auth/callback'
	}

	// For Vercel preview deployments
	if (window.location.hostname.includes('vercel.app')) {
		console.log('[getURL] Matched Vercel preview domain')
		return `${window.location.origin}/auth/callback`
	}

	// For development and other environments
	if (
		window.location.hostname.includes('localhost') ||
		window.location.hostname.includes('127.0.0.1')
	) {
		console.log('[getURL] Matched localhost')
		return `${window.location.origin}/auth/callback`
	} else {
		// Ensure https for non-localhost
		console.log('[getURL] Matched other domain')
		const url = window.location.origin.startsWith('http://')
			? window.location.origin.replace('http://', 'https://')
			: window.location.origin
		return `${url}/auth/callback`
	}
}

const getFormSchema = (t: any) => z.object({
	email: z
		.string()
		.email(t('auth.invalidEmailAddress'))
		.regex(emailRegex, t('auth.pleaseEnterValidEmailAddress'))
		.min(5, t('auth.emailMinLength'))
		.max(255, t('auth.emailMaxLength')),
})

const RESEND_COOLDOWN_SEC = 120

const Auth = () => {
	const { t } = useTranslation()
	const [isLoading, setIsLoading] = useState(false)
	const [magicLinkSent, setMagicLinkSent] = useState(false)
	const [isResending, setIsResending] = useState(false)
	const [resendCountdown, setResendCountdown] = useState<number | null>(null)
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirect')
	const { toast } = useToast()
	const formSchema = getFormSchema(t)

	// Note: Auth state changes are now handled by AuthContext and AuthCallback page
	// Removed duplicate listener to prevent race conditions and navigation conflicts

	// Check if user is already logged in
	useEffect(() => {
		const checkAuth = async () => {
			const {
				data: { user },
			} = await supabase.auth.getUser()
			if (user) {
				// Redirect based on parameter if user is already logged in
				if (redirectTo === 'pricing') {
					navigate('/settings?tab=billing')
				} else {
					navigate('/dashboard')
				}
			}
		}
		checkAuth()
	}, [navigate, redirectTo])

	// Countdown timer for resend email
	useEffect(() => {
		if (resendCountdown === null) return
		if (resendCountdown === 0) return
		const interval = setInterval(() => {
			setResendCountdown((prev) => (prev && prev > 0 ? prev - 1 : 0))
		}, 1000)
		return () => clearInterval(interval)
	}, [resendCountdown])

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: '',
		},
	})

	const onSubmit = async (values: z.infer<typeof formSchema>) => {
		setIsLoading(true)
		try {
			const redirectUrl = getURL()

			const { error } = await supabase.auth.signInWithOtp({
				email: values.email,
				options: {
					emailRedirectTo: redirectUrl,
				},
			})

			if (error) throw error

			setMagicLinkSent(true)
			setResendCountdown(RESEND_COOLDOWN_SEC)
			toast({
				title: t('auth.checkYourInbox'),
				description: t('auth.magicLinkSentTo', { email: values.email }),
			})
		} catch (error: any) {
			console.error('Magic link error:', error)
			toast({
				variant: 'destructive',
				title: t('auth.error'),
				description: error.message || t('auth.errorDuringAuthentication'),
			})
		} finally {
			setIsLoading(false)
		}
	}

	const handleOAuthSignIn = async (provider: 'github' | 'google') => {
		try {
			// Get the callback URL
			let callbackUrl = getURL()

			// Add redirect parameter if needed
			if (redirectTo) {
				callbackUrl += `?redirect=${redirectTo}`
			}

			const { error } = await supabase.auth.signInWithOAuth({
				provider,
				options: {
					redirectTo: callbackUrl,
				},
			})

			if (error) {
				console.error(`[Auth] ❌ ${provider} OAuth error:`, error)
				throw error
			}

			// The authentication will be handled by AuthCallback page
		} catch (error: any) {
			console.error(`[Auth] ${provider} sign-in error:`, error)
			toast({
				variant: 'destructive',
				title: t('auth.error'),
				description: t('auth.failedToSignInWith', { provider: provider.charAt(0).toUpperCase() + provider.slice(1) }),
			})
		}
	}

	const handleResendMagicLink = async () => {
		if (!form.getValues('email')) return

		setIsResending(true)
		try {
			const redirectUrl = getURL()

			const { error } = await supabase.auth.signInWithOtp({
				email: form.getValues('email'),
				options: {
					emailRedirectTo: redirectUrl,
				},
			})

			if (error) throw error

			toast({
				title: t('auth.magicLinkResent'),
				description: t('auth.checkInboxAgain'),
			})
			// Immediately restart cooldown so the button disappears right away
			setResendCountdown(RESEND_COOLDOWN_SEC)
		} catch (error: any) {
			console.error('Resend magic link error:', error)
			toast({
				variant: 'destructive',
				title: t('auth.error'),
				description: error.message || t('auth.failedToResendMagicLink'),
			})
		} finally {
			setIsResending(false)
		}
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#ffffff] to-[#866bfe2a] p-4 w-full relative">
			<Helmet>
				<title>{`${t('header.logIn')} | Feedbask`}</title>
			</Helmet>
			<div className="absolute top-6 right-6">
				<LanguageSwitcher />
			</div>
			<Card className="w-full max-w-md rounded-2xl shadow-xl border-0 bg-white dark:bg-gray-900">
				<CardHeader className="text-center p-6">
					<CardTitle className="text-2xl font-bold flex items-center justify-center gap-2 text-gray-900 dark:text-gray-100">
						<Code className="h-5 w-5 text-primary" />
						{t('auth.welcomeToFeedbask')}
					</CardTitle>
					
				</CardHeader>
				<CardContent className="p-6 space-y-4">
					{magicLinkSent ? (
						<div className="py-6 sm:py-8 text-center">
							<Mail className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-green-500 mb-3 sm:mb-4" />
							<h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">
								{t('auth.magicLinkSent')}
							</h3>
							<p className="text-sm text-gray-600 dark:text-gray-400 mt-2 px-2">
								{t('auth.signInLinkSentTo', { email: form.getValues('email') })}
							</p>
							{resendCountdown !== null && resendCountdown > 0 ? (
								<p className="text-xs mt-3 sm:mt-4 text-gray-500 dark:text-gray-400">
									{t('auth.didntReceiveEmail')}{' '}
									<span className="font-medium">
										{Math.floor(resendCountdown / 60)}:
										{String(resendCountdown % 60).padStart(2, '0')}
									</span>
								</p>
							) : (
								<Button
									variant="link"
									className="mt-3 sm:mt-4 h-auto p-0 text-primary hover:underline"
									onClick={handleResendMagicLink}
									disabled={isResending}>
									{isResending && (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									)}
									{t('auth.resendEmail')}
								</Button>
							)}
						</div>
					) : (
						<div className="space-y-3">
							<Button
								variant="outline"
								type="button"
								className="w-full h-12 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg flex items-center justify-center gap-2 font-medium"
								onClick={() => handleOAuthSignIn('google')}
								disabled={isLoading}>
								<svg
									className="h-5 w-5"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg">
									<path
										d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
										fill="#4285F4"
									/>
									<path
										d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
										fill="#34A853"
									/>
									<path
										d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
										fill="#FBBC05"
									/>
									<path
										d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
										fill="#EA4335"
									/>
								</svg>
								{t('auth.continueWithGoogle')}
							</Button>

							<div className="relative">
								<div className="absolute inset-0 flex items-center">
									<Separator className="bg-gray-300 dark:bg-gray-600" />
								</div>
								<div className="relative flex justify-center text-xs uppercase">
									<span className="bg-white dark:bg-gray-900 px-2 text-gray-600 dark:text-gray-400">
										{t('auth.orContinueWithEmail')}
									</span>
								</div>
							</div>

							<Form {...form}>
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="space-y-4">
									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-gray-700 dark:text-gray-300">
													{t('auth.emailAddress')}
												</FormLabel>
												<FormControl>
													<Input
														type="email"
														placeholder={t('auth.emailPlaceholder')}
														{...field}
														className="bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700 focus:border-primary focus:ring-primary h-12 w-full rounded-lg"
														disabled={isLoading}
														required
													/>
												</FormControl>
												<FormMessage className="text-red-500 dark:text-red-400" />
											</FormItem>
										)}
									/>
									<Button
										type="submit"
										className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-medium"
										disabled={isLoading || !form.watch('email')}>
										{isLoading ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												{t('auth.sending')}
											</>
										) : (
											<>
												<Mail className="mr-2 h-4 w-4" />
												{t('auth.sendMagicLink')}
											</>
										)}
									</Button>
								</form>
							</Form>
							<AuthLegalNotice className="pt-2 text-center" />
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	)
}

export default Auth
